import { selectionToBins } from 'features/fde/binningUtils'
import { FigureSelectionArea } from 'features/fde/regionTypes'
import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import {
  DamageCategoryOptions,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeneratedDamageCategory,
} from 'types'

// --------------------- Constants ---------------------

const ALGORITHM_VERSION = '1.0'

export const AREA_THRESHOLD = 2 * 2 // in feet

export const consequenceMappings: Record<DamageCategoryOptions, number> = {
  [DamageCategoryOptions.RUPTURE]: 1,
  [DamageCategoryOptions.PINHOLE_LEAK]: 0.5,
  [DamageCategoryOptions.NONE]: 0,
  [DamageCategoryOptions.UNKNOWN]: 0,
}

export const probabilityDiscountFactorMapping: Record<
  DamageCategoryOptions,
  number
> = {
  [DamageCategoryOptions.RUPTURE]: 1,
  [DamageCategoryOptions.PINHOLE_LEAK]: 0.5,
  [DamageCategoryOptions.NONE]: 0,
  [DamageCategoryOptions.UNKNOWN]: 0,
}

// --------------------- Categorization Functions ---------------------

/**
 * Categorize the indication based on the area of the selection
 * If the area is less than or equal to 4 square feet, it is a pinhole leak
 * Otherwise, it is a rupture
 *
 * TODO: Add in other parameters into the categorization function
 */

const calculateArea = (selectionArea: FigureSelectionArea) => {
  const { x: xRange, y: yRange } = selectionArea
  const xRangeFT = xRange.delta.convertTo(GeckoMeasurementUnit.Feet)
  const yRangeFT = yRange.delta.convertTo(GeckoMeasurementUnit.Feet)
  const area = xRangeFT.num * yRangeFT.num
  return area
}

const categorizeIndication = (area: number) => {
  if (area <= AREA_THRESHOLD) {
    return DamageCategoryOptions.PINHOLE_LEAK
  } else {
    return DamageCategoryOptions.RUPTURE
  }
}

// --------------------- Probability Calculation ---------------------

/**
 *
 * Probability = Percent of defect area in filter range * Discount factor
 *
 * TODO: figure out how to incorporate the following into the function:
 *  Difference from TMin
 * Line Averaged Thickness
 *
 * Where:
 * - Count of values in filter range = Number of values within the specified filter range
 * - Total count of values = Total number of values in the selected area
 * - Percent of defect area = Count of values in filter range / Total count of values
 * - Discount factor = A predefined factor based on the damage category
 */

const getCDFInBounds = (
  cdf: GeckoComponentDataFrame,
  selectionArea: FigureSelectionArea,
  cdfRawData: readonly (readonly number[])[]
) => {
  const selectedBins = selectionToBins(
    selectionArea,
    cdf.data.binning,
    cdf.data.geometry
  )
  return cdfRawData
    .map((row) =>
      row.slice(selectedBins.minCorner[0], selectedBins.maxCorner[0])
    )
    .slice(selectedBins.minCorner[1], selectedBins.maxCorner[1])
}

const percentInRange = (
  selectionArea: FigureSelectionArea,
  cdf: GeckoComponentDataFrame,
  filterRange: GeckoMeasurementRange,
  cdfRawData: readonly (readonly number[])[]
) => {
  const cdfInBounds = getCDFInBounds(cdf, selectionArea, cdfRawData)
  const cdfInBoundsFlat = cdfInBounds.flat()
  const countInRange = cdfInBoundsFlat.filter(
    (value) => value >= filterRange.start.num && value <= filterRange.end.num
  ).length
  const percentInRange = countInRange / cdfInBoundsFlat.length
  const xDeltaFt = selectionArea.x.delta.convertTo(GeckoMeasurementUnit.Feet)
  const yDeltaFt = selectionArea.y.delta.convertTo(GeckoMeasurementUnit.Feet)
  const defectArea = percentInRange * xDeltaFt.num * yDeltaFt.num
  return {
    percentInRange,
    defectArea,
  }
}

const calculateProbability = (
  percentInFilterRange: { percentInRange: number; defectArea: number },
  damageCategory: DamageCategoryOptions
) => {
  //replace percent in filter range by the area in filterange
  const discountFactor = probabilityDiscountFactorMapping[damageCategory]
  return percentInFilterRange.percentInRange * discountFactor
}

// --------------------- Consequence Calculation ---------------------
/**
 * Consequence = Elevation Ratio * Category Consequence
 *
 * Where:
 * - Elevation Ratio = (Height of the component - Middle of the selection area) / Height of the component
 * - Category Consequence = A predefined consequence value based on the damage category
 */

const calculateElevationRatio = (
  selectionArea: FigureSelectionArea,
  componentModel?: GeckoComponentModel
) => {
  if (
    !componentModel ||
    componentModel.type !== GeckoComponentModelType.VerticalTankShell
  ) {
    return 1
  }
  const { height } = componentModel
  const { y: yRange } = selectionArea
  const heightFT = height.convertTo(GeckoMeasurementUnit.Feet)
  const yMiddle = yRange.start.plus(yRange.end).scale(0.5)
  const yMiddleFT = yMiddle.convertTo(GeckoMeasurementUnit.Feet)
  const distanceFromTop = heightFT.num - yMiddleFT.num
  return distanceFromTop / heightFT.num
}

const calculateConsequence = (
  elevationRatio: number,
  category: DamageCategoryOptions
) => {
  const categoryConsequence = consequenceMappings[category]
  return categoryConsequence * elevationRatio
}

export const calculateDamageArea = (
  indication: IndicationItem,
  cdf: GeckoComponentDataFrame,
  componentModel?: GeckoComponentModel,
  filterRange?: GeckoMeasurementRange,
  cdfRawData?: readonly (readonly number[])[],
  precision: number = 6
) => {
  const damageCategory: GeneratedDamageCategory = {
    category: DamageCategoryOptions.UNKNOWN,
    confidence: 0,
    probability: 0,
    consequence: 0,
    version: ALGORITHM_VERSION,
  }
  if (!indication.selection) {
    return damageCategory
  }

  const area = calculateArea(indication.selection)
  const category = categorizeIndication(area)
  const elevationRatio = calculateElevationRatio(
    indication.selection,
    componentModel
  )
  damageCategory.category = category
  const consequence = calculateConsequence(elevationRatio, category)
  if (consequence) {
    //add the consequence fields to the damageCategory object
    damageCategory.area = parseFloat(area.toFixed(precision))
    damageCategory.elevationRatio = parseFloat(
      elevationRatio.toFixed(precision)
    )
    damageCategory.consequence = parseFloat(consequence.toFixed(precision))
  }

  if (cdfRawData && filterRange) {
    const percentInFilterRange = percentInRange(
      indication.selection,
      cdf,
      filterRange,
      cdfRawData
    )
    const probability = calculateProbability(percentInFilterRange, category)
    if (probability) {
      //add the probability fields to the damageCategory object
      damageCategory.percentInRange = parseFloat(
        percentInFilterRange.percentInRange.toFixed(precision)
      )
      damageCategory.probability = parseFloat(probability.toFixed(precision))
    }
  }

  return damageCategory
}
